import { z } from 'zod'

import {
  getEntityRegistry,
  isUpdatable,
} from '@/Atomic/Widgets/AiChatBot/core/engine/entity-registry'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import {
  checkRateLimit,
  recordCall,
} from '@/Atomic/Widgets/AiChatBot/core/engine/pipeline/write/rate-limiter'
import { clearToolCache } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-cache'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import type {
  SessionContext,
  ShowOptionsResult,
  ToolResultContent,
} from '@/Atomic/Widgets/AiChatBot/core/engine/types'
import { mapFieldsFromIntent } from '@/Atomic/Widgets/AiChatBot/core/tools/schema/field-mapper'
import {
  requireDataClassName,
  resolveDataClass,
  type ScrivitoAccessor,
} from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'

type UpdateEntityArgs = {
  type: string
  id: string
  /** Diff-only: only the fields the user explicitly mentioned. */
  fields: Record<string, string>
}

/**
 * Update an existing CRM entity record.
 *
 * Diff-only invariant: only the fields the user explicitly mentioned are sent to PISA.
 * Field validity is determined by the Scrivito DataClass schema — no hardcoded whitelist.
 * Pauses the pipeline and shows a ConfirmationCard — the write fires only after the user clicks Confirm.
 *
 * concurrencySafe: false — updates must never run concurrently.
 */
export const updateEntityTool = defineTool({
  name: 'update_entity',
  description:
    'Update an existing CRM record. Only the fields you explicitly specify are changed — other fields are untouched. ' +
    'For relation fields always pass the record _id — NEVER a display name. To set a person/owner reference field to the acting user ("make me responsible", "assign it to me"), pass the literal "@currentUser" as that field\'s value — do NOT ask the user for their name and do NOT guess or copy an id. ' +
    'Shows a confirmation card for the user to approve before writing. ' +
    'Do NOT pass a confirmed parameter — confirmation is handled by the UI.',
  inputSchema: () =>
    z.object({
      type: z
        .string()
        .describe('Entity type, e.g. Ticket, Activity, Opportunity'),
      id: z.string().describe('The _id of the record to update'),
      fields: z
        .record(z.string(), z.string())
        .describe(
          'Only the fields to change (diff-only). Fields not in this object are untouched.',
        ),
    }),
  concurrencySafe: false,
  cacheable: false,

  async run(
    rawArgs: unknown,
    ctx: SessionContext,
    toolCallId?: string,
  ): Promise<ToolResultContent | { error: string }> {
    // FunctionDefinition<unknown> contravariance — cast args inside run(), per project pattern
    const args = rawArgs as UpdateEntityArgs
    const { type: rawType, id, fields } = args

    const entityType = requireDataClassName(rawType)
    if (!entityType) {
      return { error: `Unknown entity type: ${rawType}` }
    }

    if (!isUpdatable(entityType)) {
      return { error: `Updating ${entityType} records is not supported.` }
    }

    const rateCheck = checkRateLimit('updates')
    if (!rateCheck.allowed) {
      return { kind: 'text', content: rateCheck.message ?? '' }
    }

    // Load the record up front: its current controlling codes (group/type/subType)
    // let the field mapper resolve co-dependent enum labels to the right code, and
    // the same item drives the diff card below.
    const dataClass = resolveDataClass(entityType)
    if (!dataClass) {
      return { error: `DataClass not found for ${entityType}` }
    }
    // Mirror getDataItem's swallow-on-error contract: a missing record or an
    // access-denied/unknown fetch error both surface as "not found" here, rather
    // than propagating — the adapter's get() would otherwise throw on those.
    let dataItem: ScrivitoAccessor | null = null
    try {
      dataItem =
        ((await getHostRuntime().data.get(
          entityType,
          id,
        )) as ScrivitoAccessor | null) ?? null
    } catch {
      dataItem = null
    }
    if (!dataItem) {
      return { error: `${entityType} with id ${id} not found.` }
    }

    const mapping = await mapFieldsFromIntent({
      entityType,
      intentParams: fields,
      operation: 'update',
      record: dataItem,
      fieldGroups: getEntityRegistry()[entityType]?.fieldGroups,
      currentUserId: ctx.currentUserId,
    })
    if (!mapping.ok) {
      return { error: mapping.message }
    }
    // Recognised fields that are frozen after creation (e.g. Activity main type).
    // Report this distinctly so the model tells the user the field can't be changed
    // — instead of the misleading "not recognised", which makes it retry other keys.
    if (mapping.immutable.length > 0) {
      const labels = mapping.immutable.map((f) => f.label).join(', ')
      return {
        error: `These fields can't be changed after a ${entityType} is created: ${labels}. Tell the user this field is fixed once the record exists — it can only be set when creating a new record.`,
      }
    }
    if (mapping.invalid.length > 0) {
      return {
        error: `The following fields were not recognised: ${mapping.invalid.join(', ')}.`,
      }
    }
    // A reference id that resolves to no live record (a stale or guessed id) must
    // never be written — it would persist a dangling reference and show a raw id on
    // the card. Reject so the model re-resolves the record via search_entities.
    if (mapping.unresolvedReferences.length > 0) {
      const labels = mapping.unresolvedReferences.map((r) => r.label).join(', ')
      return {
        error: `Could not find the record referenced by ${labels}. Look it up with search_entities and pass the matching record's _id — do not guess an id.`,
      }
    }
    // Enum values that matched no option (e.g. user asked for "active" but this record's
    // group has no such status). Resolve each one with its own selection card here, in
    // this tool run — the model never re-enters the loop, so it never narrates the
    // ambiguity. Current value is excluded (re-selecting it is a no-op). Cancelling any
    // picker cancels the whole update. Picks are merged into the mapping below so they
    // flow into the single confirmation card unchanged.
    for (const info of mapping.invalidValues) {
      const currentCode = dataItem.get(info.key)
      const options = info.options
        .filter((o) => o.value !== currentCode)
        .map((o) => ({ value: o.value, label: o.title }))
      const pickId = `${toolCallId ?? '_unknown'}:${info.key}`
      const picked = await new Promise<ShowOptionsResult>((resolve, reject) => {
        ctx.registerToolCallResolver?.(
          pickId,
          {
            type: 'options',
            toolCallId: pickId,
            question: `Which ${info.label.toLowerCase()} do you want to set?`,
            options,
          },
          resolve as (result: unknown) => void,
          reject,
        )
      })
      if (picked.cancelled || !('selected' in picked) || !picked.selected[0]) {
        return { kind: 'text', content: `Cancelled updating ${entityType}.` }
      }
      const chosen = info.options.find((o) => o.value === picked.selected[0])
      if (!chosen) {
        return { kind: 'text', content: `Cancelled updating ${entityType}.` }
      }
      mapping.mapped[info.key] = chosen.value
      mapping.displayMapped[info.key] = chosen.title
      mapping.labelMap[info.key] = info.label
      mapping.enumDisplayMap[info.key] = Object.fromEntries(
        info.options.map((o) => [o.value, o.title]),
      )
    }

    // Build diff rows: label = localized field name, value = "current → new"
    const rows = Object.entries(mapping.displayMapped).map(
      ([fieldName, displayValue]) => {
        const currentValue = dataItem.get(fieldName)
        let currentStr: string
        const currentRefName = mapping.currentDisplayMap[fieldName]
        if (currentRefName !== undefined) {
          // Reference field — name resolved by id in the field mapper (never a raw id).
          currentStr = currentRefName
        } else if (currentValue === null || currentValue === undefined) {
          currentStr = '—'
        } else {
          const enumMap = mapping.enumDisplayMap[fieldName]
          currentStr =
            enumMap !== undefined
              ? (enumMap[String(currentValue)] ?? String(currentValue))
              : String(currentValue)
        }
        return {
          label: mapping.labelMap[fieldName] ?? fieldName,
          value: `${currentStr} → ${displayValue}`,
          source: 'resolved' as const,
        }
      },
    )

    // Pause pipeline — show ConfirmationCard
    const toolId = toolCallId ?? '_unknown'
    const confirmed = await new Promise<boolean>((resolve, reject) => {
      ctx.registerToolCallResolver?.(
        toolId,
        {
          type: 'confirm',
          toolCallId: toolId,
          spec: {
            title: `Update ${entityType}`,
            rows,
          },
        },
        (result: unknown) => resolve(result === true),
        reject,
      )
    })

    if (!confirmed) {
      return { kind: 'text', content: `Cancelled updating ${entityType}.` }
    }

    try {
      await getHostRuntime().data.update(dataItem, mapping.mapped)
      recordCall('updates')
      clearToolCache()
      // Refresh the app's query caches (incl. relation classes that back
      // detail-page tabs) so the edit shows without a manual reload.
      getHostRuntime().data.invalidate(entityType)
      // Self-describing result: name every field actually written with its final
      // display value, so the model relays the real outcome instead of parroting
      // the user's original request (which may differ — e.g. a value resolved via
      // the selection card above).
      const changes = Object.entries(mapping.displayMapped)
        .map(
          ([field, value]) => `${mapping.labelMap[field] ?? field}: ${value}`,
        )
        .join(', ')
      return {
        kind: 'text',
        content: `Updated ${entityType} — ${changes}.`,
      }
    } catch {
      return {
        kind: 'text',
        content: `Failed to update ${entityType} — please try again.`,
      }
    }
  },
})
